import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { X } from 'lucide-react';
import * as THREE from 'three';
import PrivacyHouseVisibility from './PrivacyHouseVisibility';
import type { SparkViewer } from '../utils/SparkViewer';
import { MEDICAL_LAKE_NEIGHBOR_WINDOWS as SOURCES, MEDICAL_LAKE_PRIVACY_WINDOWS as WINDOWS, MEDICAL_LAKE_FRONT_WINDOWS as FRONT_WINDOWS, MEDICAL_LAKE_PRIVACY_VIEWPOINTS } from '../config/medicalLakePrivacy';
import { createPrivacyWindowTour, updatePrivacyWindowTour, privacyWindowsCameraView, privacyWindowLabelPosition, privacyWindowScreenLabel, projectToScreen, worldDistance3D } from '../utils/measureUtils';

const HOUSE_OPTIONS = [
  { id: 'rear-house', label: 'House 4 · rear', sources: SOURCES.filter(source => source.id.startsWith('neighbor-')) },
  { id: 'house-2', label: 'House 2', sources: SOURCES.filter(source => source.id === 'front-house-2') },
  { id: 'house-3', label: 'House 3', sources: SOURCES.filter(source => source.id.startsWith('front-house-3-')) },
];

export default function RealEstatePrivacyOverlay({ viewerRef, onClose }: {
  viewerRef: MutableRefObject<unknown>;
  onClose: () => void;
}) {
  const [presentation, setPresentation] = useState<'overview' | 'flight' | 'detail'>('overview');
  const [neighborView, setNeighborView] = useState(false);
  const flightFrame = useRef(0);
  const flightRunning = useRef(false);
  const [selectedSource, setSelectedSource] = useState('all');
  const selectedHouse = HOUSE_OPTIONS.find(house => house.sources.some(source => source.id === selectedSource)) ?? HOUSE_OPTIONS[0];
  const introPending = useRef(true);
  const parcelCorners = useRef<[number, number, number][]>([]);
  const labels = useRef<Record<string, HTMLButtonElement | null>>({});
  useEffect(() => {
    const scene = (viewerRef.current as SparkViewer | null)?.scene;
    if (!scene) return;
    const background = scene.background;
    const sky = new THREE.Color(0xa7bac7);
    scene.background = sky;
    return () => { if (scene.background === sky) scene.background = background; };
  }, [viewerRef]);
  const stopFlight = useCallback(() => {
    cancelAnimationFrame(flightFrame.current);
    if (flightRunning.current) (viewerRef.current as SparkViewer | null)?.setScriptedCameraActive(false);
    flightRunning.current = false;
    setPresentation(current => current === 'flight' ? 'detail' : current);
  }, [viewerRef]);
  const flyToNeighbor = useCallback((sourceOverride?: string, fromSourceOverride?: string) => {
    stopFlight();
    const viewer = viewerRef.current as SparkViewer | null;
    // Start from the rear house in overview; preserve an explicitly selected opening.
    const source = SOURCES.find(opening => opening.id === (sourceOverride ?? selectedSource))
      ?? SOURCES.find(opening => opening.id === 'neighbor-1')
      ?? SOURCES[0];
    if (!viewer) return;
    introPending.current = false;
    // Front neighbors should fly toward the front facade openings. House 4 is
    // behind the property and keeps the established rear-window destination.
    const targets = source.id.startsWith('front-house-') ? FRONT_WINDOWS : WINDOWS;
    const destination = source.id === 'front-house-2'
      ? MEDICAL_LAKE_PRIVACY_VIEWPOINTS.house2
      : source.id.startsWith('front-house-3-')
        ? MEDICAL_LAKE_PRIVACY_VIEWPOINTS.house3
        : source.id.startsWith('neighbor-')
          ? MEDICAL_LAKE_PRIVACY_VIEWPOINTS.rearHouse
          : undefined;
    const comingFromRearHouse = (fromSourceOverride ?? selectedSource).startsWith('neighbor-');
    const tour = createPrivacyWindowTour(viewer.camera, source.corners, targets.map(opening => opening.corners), destination,
      comingFromRearHouse && source.id.startsWith('front-house-3-'));
    viewer.setScriptedCameraActive(true);
    flightRunning.current = true;
    setNeighborView(true);
    setPresentation('flight');
    setSelectedSource(source.id);
    let elapsed = 0, previous: number | null = null;
    const animate = (now: number) => {
      if (!flightRunning.current) return;
      const delta = previous === null ? 0 : Math.min(now - previous, 100);
      previous = now;
      if (!document.hidden) elapsed += delta;
      const progress = Math.min(elapsed / 9000, 1);
      updatePrivacyWindowTour(viewer.camera, tour, progress);
      viewer.syncManualNavigation();
      if (progress === 1) { stopFlight(); return; }
      flightFrame.current = requestAnimationFrame(animate);
    };
    flightFrame.current = requestAnimationFrame(animate);
  }, [viewerRef, stopFlight, selectedSource]);
  const selectHouse = useCallback((houseId: string) => {
    const house = HOUSE_OPTIONS.find(option => option.id === houseId);
    if (!house?.sources[0]) return;
    const previousHouse = HOUSE_OPTIONS.find(option => option.sources.some(source => source.id === selectedSource));
    const previousSource = selectedSource;
    setSelectedSource(house.sources[0].id);
    const isSharedFrontPair = [previousHouse?.id, house.id].includes('house-2') && [previousHouse?.id, house.id].includes('house-3');
    if (neighborView && !isSharedFrontPair) requestAnimationFrame(() => flyToNeighbor(house.sources[0].id, previousSource));
  }, [neighborView, flyToNeighbor, selectedSource]);
  useEffect(() => {
    const canvas = (viewerRef.current as SparkViewer | null)?.renderer.domElement;
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement | null)?.closest('input, textarea, select')) return;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'Escape'].includes(event.code)) stopFlight();
    };
    canvas?.addEventListener('pointerdown', stopFlight, true);
    canvas?.addEventListener('wheel', stopFlight, { capture: true, passive: true });
    window.addEventListener('keydown', onKey, true);
    return () => {
      stopFlight();
      canvas?.removeEventListener('pointerdown', stopFlight, true);
      canvas?.removeEventListener('wheel', stopFlight, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [viewerRef, stopFlight]);
  const fitView = useCallback(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !canvas) return;
    stopFlight();
    setNeighborView(false);
    setPresentation('overview');
    setSelectedSource('all');
    const rect = canvas.getBoundingClientRect();
    const panel = document.querySelector('[data-privacy-controls]')?.getBoundingClientRect();
    const inset = panel ? Math.max(0, panel.right + 16 - rect.left) / Math.max(rect.width, 1) : 0;
    void viewer.teleportTo(privacyWindowsCameraView(
      SOURCES.flatMap(source => source.corners), [...WINDOWS.filter(window => !window.reviewReason).map(window => window.corners), parcelCorners.current],
      canvas.clientWidth / Math.max(canvas.clientHeight, 1), inset,
    ));
  }, [viewerRef, stopFlight]);
  useEffect(() => {
    const canvas = (viewerRef.current as SparkViewer | null)?.renderer?.domElement;
    if (!canvas) return;
    // Geometry establishes the initial framing before any casts are drawn.
    // Resize notifications must not perform another initial fit while it loads.
    let width = canvas.clientWidth, height = canvas.clientHeight;
    const observer = new ResizeObserver(() => {
      const changed = width !== canvas.clientWidth || height !== canvas.clientHeight;
      width = canvas.clientWidth; height = canvas.clientHeight;
      if (changed && !introPending.current && !flightRunning.current) fitView();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [viewerRef, fitView]);
  useEffect(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    if (!viewer) return;
    const scene = viewer.scene;
    const previous = scene.onAfterRender;
    const afterRender: THREE.Scene['onAfterRender'] = function (this: THREE.Scene, ...args) {
      previous.apply(this, args);
      const canvas = viewer.renderer.domElement;
      const rect = canvas.getBoundingClientRect();
      for (const source of SOURCES) {
        const element = labels.current[source.id];
        if (!element) continue;
        const anchor = privacyWindowLabelPosition(source.corners);
        const point = presentation === 'detail' ? privacyWindowScreenLabel(anchor, args[2], canvas) : { ...projectToScreen(anchor, args[2], canvas), offscreen: false, arrow: '' };
        const revealed = presentation === 'detail' && source.id === selectedSource;
        element.style.visibility = revealed && point.visible && worldDistance3D(anchor, args[2].position) > 2 ? 'visible' : 'hidden';
        element.textContent = point.offscreen ? `${source.label} ${point.arrow}` : source.label;
        element.title = point.offscreen ? `${source.label} is off-screen in this direction` : source.label;
        element.style.translate = `${point.x + rect.left}px ${point.y + rect.top}px`;
      }
    };
    scene.onAfterRender = afterRender;
    return () => { if (scene.onAfterRender === afterRender) scene.onAfterRender = previous; };
  }, [viewerRef, presentation, selectedSource]);


  const onSurfacesReady = useCallback((ring: [number, number, number][]) => {
    parcelCorners.current = ring;
    if (!introPending.current) return;
    fitView();
    introPending.current = false;
  }, [fitView]);

  return <>
    {SOURCES.map(source => <button key={source.id} ref={element => { labels.current[source.id] = element; }}
      type="button" data-privacy-window-label={source.id} onClick={() => setSelectedSource(source.id)}
      className="fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-blue-200/70 bg-[#124b91]/95 px-2 py-1 text-[10px] font-medium text-sky-50 shadow-lg backdrop-blur-sm hover:bg-[#1761b5]"
      style={{ visibility: 'hidden', transform: 'translate(-50%, -100%)' }}>{source.label}</button>)}
    <section data-privacy-controls aria-label="Window privacy sightlines" className="fixed left-4 top-4 z-[1003] max-h-[calc(100vh-2rem)] w-64 overflow-y-auto rounded-xl border border-sky-200/30 bg-[#1b3040]/95 p-3 text-[11px] text-sky-50/85 shadow-xl backdrop-blur-md">
    <header className="mb-2 flex items-center justify-between border-b border-sky-200/15 pb-2">
      <h2 className="text-xs font-semibold text-sky-100">Privacy</h2>
      <button type="button" onClick={onClose} aria-label="Close privacy" className="rounded p-1 text-sky-100/60 hover:text-white"><X size={14} /></button>
    </header>
    <div className="font-semibold text-sky-100">Neighbor openings → this property</div>
    {presentation === 'detail' && <label className="mt-2 block text-[10px] text-sky-100/70">View from house
      <select value={selectedHouse.id} onChange={event => selectHouse(event.target.value)}
        className="mt-1 block w-full rounded-lg border border-white/20 bg-[#172a38] px-2 py-1.5 text-[11px] text-sky-50">
        {HOUSE_OPTIONS.filter(house => house.sources.length > 0).map(house => <option key={house.id} value={house.id}>{house.label}</option>)}
      </select>
    </label>}
    {presentation === 'detail' && selectedHouse.sources.length > 1 && <label className="mt-2 block text-[10px] text-sky-100/70">Viewpoint
      <select value={selectedSource} onChange={event => setSelectedSource(event.target.value)}
        className="mt-1 block w-full rounded-lg border border-white/20 bg-[#172a38] px-2 py-1.5 text-[11px] text-sky-50">
        {selectedHouse.sources.map(source => <option key={source.id} value={source.id}>{source.label}</option>)}
      </select>
    </label>}
    {presentation !== 'detail' && <p className="mt-2 text-[10px] text-sky-100/70">{presentation === 'flight' ? 'Moving to the neighbor’s house…' : 'Combined view from all visible openings'}</p>}
    <PrivacyHouseVisibility viewerRef={viewerRef} sourceId={selectedSource} revision={0} onReady={onSurfacesReady} presentation={presentation} />
    {neighborView ? <>
      <button type="button" className="mt-2 w-full rounded-lg border border-sky-200/30 bg-sky-400/10 px-2 py-2 font-medium text-sky-50 hover:bg-sky-400/20"
        onClick={fitView}>Back to overview</button>
    </> : <button type="button" className="mt-2 w-full rounded-lg border border-sky-200/30 bg-sky-400/10 px-2 py-2 font-medium text-sky-50 hover:bg-sky-400/20"
      onClick={flyToNeighbor}>View from neighbor’s house</button>}
    <p className="mt-3 text-[10px] leading-relaxed text-sky-100/55">Visibility is checked from the measured neighbor opening, using the loaded exterior scan. Interiors, curtains and glazing aren’t verified.</p>
  </section>
  </>;
}
